import { useState, useEffect, useCallback } from 'react';
import { Upload, Search, FolderOpen, CheckSquare, Square, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  getBrowserBookmarks,
  importBrowserBookmarks,
  flattenBookmarks,
  isExtensionAvailable,
  type ImportBookmarkItem,
} from '../lib/extension';
import type { Collection } from '../types/database';

interface FlattenedBookmark extends ImportBookmarkItem {
  id: string;
  selected: boolean;
}

interface ImportBrowserBookmarksProps {
  collections: Collection[];
  onImportComplete: () => void;
}

export function ImportBrowserBookmarks({
  collections,
  onImportComplete,
}: ImportBrowserBookmarksProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [extensionAvailable, setExtensionAvailable] = useState<boolean | null>(null);
  const [bookmarks, setBookmarks] = useState<FlattenedBookmark[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [createCollections, setCreateCollections] = useState(true);
  const [targetCollectionId, setTargetCollectionId] = useState<string>('');
  const [result, setResult] = useState<{ imported: number; failed: number } | null>(null);

  // Check extension availability when dialog opens
  useEffect(() => {
    if (open) {
      checkExtension();
    }
  }, [open]);

  const checkExtension = async () => {
    const available = await isExtensionAvailable();
    setExtensionAvailable(available);
    if (available) {
      loadBookmarks();
    }
  };

  const loadBookmarks = async () => {
    setLoading(true);
    try {
      const tree = await getBrowserBookmarks();
      const flattened = flattenBookmarks(tree);
      setBookmarks(
        flattened.map((b, index) => ({
          ...b,
          id: `bookmark-${index}`,
          selected: true,
        }))
      );
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
      toast.error(error instanceof Error ? error.message : '북마크를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  // Toggle bookmark selection
  const toggleBookmark = (id: string) => {
    setBookmarks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, selected: !b.selected } : b))
    );
  };

  // Select/deselect all
  const toggleAll = (selected: boolean) => {
    setBookmarks((prev) => prev.map((b) => ({ ...b, selected })));
  };

  // Filter bookmarks by search query
  const filteredBookmarks = bookmarks.filter(
    (b) =>
      b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.folderPath.some((f) => f.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedCount = bookmarks.filter((b) => b.selected).length;

  // Import selected bookmarks
  const handleImport = async () => {
    const selectedBookmarks = bookmarks.filter((b) => b.selected);

    if (selectedBookmarks.length === 0) {
      toast.error('가져올 북마크를 선택해주세요');
      return;
    }

    setImporting(true);

    try {
      const importResult = await importBrowserBookmarks({
        bookmarks: selectedBookmarks.map(({ url, title, folderPath }) => ({
          url,
          title,
          folderPath,
        })),
        createCollections: createCollections && !targetCollectionId,
        targetCollectionId: targetCollectionId || undefined,
      });

      setResult({ imported: importResult.imported, failed: importResult.failed });

      if (importResult.imported > 0) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error instanceof Error ? error.message : '가져오기에 실패했습니다');
      setResult({ imported: 0, failed: selectedBookmarks.length });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state after dialog closes
    setTimeout(() => {
      setBookmarks([]);
      setSearchQuery('');
      setCreateCollections(true);
      setTargetCollectionId('');
      setResult(null);
      setExtensionAvailable(null);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full gap-2">
          <Upload className="w-4 h-4" />
          브라우저 북마크 가져오기
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>브라우저 북마크 가져오기</DialogTitle>
        </DialogHeader>

        {/* Result view */}
        {result && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">가져오기 완료</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {result.imported}개 북마크를 가져왔습니다
              {result.failed > 0 && ` (${result.failed}개 실패)`}
            </p>
            <Button onClick={handleClose}>닫기</Button>
          </div>
        )}

        {/* Extension not available */}
        {!result && extensionAvailable === false && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              확장 프로그램 필요
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
              브라우저 북마크를 가져오려면 Markly 확장 프로그램이 필요합니다.
              Chrome 웹 스토어에서 설치해주세요.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose}>
                닫기
              </Button>
              <Button onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}>
                확장 프로그램 설치
              </Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {!result && extensionAvailable === null && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">확장 프로그램 확인 중...</p>
          </div>
        )}

        {!result && loading && extensionAvailable && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">북마크 불러오는 중...</p>
          </div>
        )}

        {/* Bookmark selection */}
        {!result && !loading && extensionAvailable && (
          <>
            {/* Search and options */}
            <div className="space-y-4 border-b pb-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="북마크 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Options */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {selectedCount}/{bookmarks.length}개 선택
                  </span>
                  <button
                    onClick={() => toggleAll(true)}
                    className="text-xs text-primary hover:underline"
                  >
                    전체 선택
                  </button>
                  <button
                    onClick={() => toggleAll(false)}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    전체 해제
                  </button>
                </div>
              </div>

              {/* Collection options */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="createCollections"
                    checked={createCollections}
                    onCheckedChange={(checked) => {
                      setCreateCollections(checked === true);
                      if (checked) setTargetCollectionId('');
                    }}
                  />
                  <Label htmlFor="createCollections" className="text-sm">
                    폴더를 컬렉션으로 생성
                  </Label>
                </div>

                {!createCollections && (
                  <Select value={targetCollectionId} onValueChange={setTargetCollectionId}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="컬렉션 선택 (선택 안함)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">선택 안함</SelectItem>
                      {collections.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Bookmark list */}
            <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
              {filteredBookmarks.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  {searchQuery ? '검색 결과가 없습니다' : '북마크가 없습니다'}
                </div>
              ) : (
                <div className="space-y-1 py-2">
                  {filteredBookmarks.map((bookmark) => (
                    <label
                      key={bookmark.id}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={bookmark.selected}
                        onCheckedChange={() => toggleBookmark(bookmark.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{bookmark.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{bookmark.url}</p>
                        {bookmark.folderPath.length > 0 && (
                          <p className="text-xs text-primary truncate flex items-center gap-1 mt-0.5">
                            <FolderOpen className="w-3 h-3" />
                            {bookmark.folderPath.join(' / ')}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t pt-4">
              <Button variant="outline" onClick={handleClose}>
                취소
              </Button>
              <Button
                onClick={handleImport}
                disabled={selectedCount === 0 || importing}
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    가져오는 중...
                  </>
                ) : (
                  `${selectedCount}개 북마크 가져오기`
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
